import { axiosReq } from "@/lib/axios";
import { captureApiError } from "@/lib/sentry-fn";
import { useMutation, useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import posthog from "posthog-js";
import { CreateThemeType } from "../types";

export const useCreateThemePosts = (onGenerate?: (data: number) => void) => {

  const request = async ({ formData, signal }: { formData: CreateThemeType, signal: AbortSignal }): Promise<{ data: { event_id: number } }> => {

    const data = {
      event_description: formData.event_description,
      activity: formData.activity,
      catalogue: "",
      event_time: formData.event_time,
      start_date: format(formData.event_date, 'yyyy-MM-dd'),
      end_date: formData.campiagn_timeline ? format(formData.campiagn_timeline, 'yyyy-MM-dd') : format(formData.event_date, 'yyyy-MM-dd'),
    }


    try {
      const response = await axiosReq.post('/api/approval/create-post/', data, { signal });
      return response.data;
    } catch (error) {

      captureApiError(error, {
        component: "usePostCreateSocialPost",
        url: `${import.meta.env.VITE_API_URL}/api/approval/create-post/`,
        httpMethod: "POST",
        apiEndpoint: "/api/approval/create-post/"
      })

      throw error
    }
  }

  const { mutate: createSocialPost, status, data: generatedTheme } = useMutation({
    mutationFn: (formData: CreateThemeType) => {
      const controller = new AbortController()
      return request({ formData, signal: controller.signal })
    },
    onSuccess: (data) => {

      if (data) {
        posthog.capture('event created', { event_created: data.data.event_id })
        if (onGenerate) {
          onGenerate(data.data.event_id)
        }
      }
    }
  });

  return { createSocialPost, status, generatedTheme }

}

export type SuggestedEvent = {
  event_id: number;
  event_type: string;
  start_date: string;
  end_date: string;
  event_name: string;
  event_description: string;
  activity: "Campaign" | "Post";
  catalogue: string;
  event_time: string;
}

export const useGetPostSuggestions = () => {
  const request = async (): Promise<{ data: SuggestedEvent[] }> => {
    try {
      const response = await axiosReq.post('api/approval/create-post-history/');
      return response.data
    } catch (error) {

      captureApiError(error, {
        component: "useGetPostSuggestion",
        url: `${import.meta.env.VITE_API_URL}/api/approval/create-post-history/`,
        httpMethod: "POST",
        apiEndpoint: "/api/approval/create-post-history/"
      })

      throw error
    }

  }

  const { data: suggestedEvents, isLoading } = useQuery({
    queryKey: ["suggested-events"],
    queryFn: request,
    gcTime: 0
  })

  return { suggestedEvents, isLoading }
}

export const useGetCreatedThemeImage = (onImageGenerate: (id: number) => void) => {

  const request = async (id: number): Promise<{
    status: string,
    data: {
      event_id: number,
      event_name: string
    }
  }> => {
    try {
      const response = await axiosReq.post('/api/approval/generate-images-event/', { event_id: id })
      return response.data
    } catch (error) {

      captureApiError(error, {
        component: "useGetCreatedEventImage",
        url: `${import.meta.env.VITE_API_URL}/api/approval/generate-images-event/`,
        httpMethod: "POST",
        apiEndpoint: "/api/approval/generate-images-event/"
      })

      throw error
    }
  }

  const { data: generatedDataWithImages, status, mutate: generateImage } = useMutation({
    mutationFn: (id: number) => request(id),
    onSuccess: (data) => {
      if (data) {
        onImageGenerate(data.data.event_id)
      }
    }
  })
  return { generateImage, generatedDataWithImages, status }
}

